import { createRef, FC, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Box, Paper, Typography } from "@mui/material";
import { buildFaviconAssets } from "../../QuoteExperience.helpers";
import CloudUploadOutlinedIcon from '@mui/icons-material/CloudUploadOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';

export const BrandImage: FC<{
    imageName: string;
    initialBaseImage: File | Blob | undefined;
    initialBaseImageUrl: string | ArrayBuffer | undefined;
    hint: string;
    onImageChange: (image: File | Blob) => void;
    onFaviconProcessed?: (pngBlob: Blob, icoBlob: Blob) => void;
    treatAsFavicon?: boolean;
    initialFaviconPngDataUri?: string | ArrayBuffer | undefined;
    initialFaviconIcoDataUri?: string | ArrayBuffer | undefined;
}> = ({
    imageName,
    initialBaseImage,
    initialBaseImageUrl,
    hint,
    onImageChange,
    onFaviconProcessed,
    treatAsFavicon,
    initialFaviconPngDataUri,
    initialFaviconIcoDataUri,
}) => {
        const inputRef = useRef<HTMLInputElement>(null);
        const [baseImage, setBaseImage] = useState<File | Blob | undefined>(initialBaseImage);
        const [baseImageUrl, setBaseImageUrl] = useState<string | ArrayBuffer | undefined>(initialBaseImageUrl);
        const [baseImageChanged, setBaseImageChanged] = useState<boolean>(false);
        const [faviconProcessed, setFaviconProcessed] = useState<boolean>(false);
        const [faviconPngDataUri, setFaviconPngDataUri] = useState<string | ArrayBuffer | null | undefined>(initialFaviconPngDataUri);
        const [faviconIcoDataUri, setFaviconIcoDataUri] = useState<string | ArrayBuffer | null | undefined>(initialFaviconIcoDataUri);
        const faviconRef = createRef<HTMLImageElement>();

        const imageSrc = useMemo(() => {
            if (baseImageUrl) return baseImageUrl;
            if (baseImage) {
                if (typeof baseImage === 'string') return baseImage;
                return URL.createObjectURL(baseImage);
            }
            return null;
        }, [baseImageUrl, baseImage]);

        const hasImage = !!imageSrc;

        const handleFaviconGeneration = useCallback(async (image: HTMLImageElement) => {
            try {
                const { pngBlob, pngDataUri, icoBlob, icoDataUri } = await buildFaviconAssets(image);

                setFaviconPngDataUri(pngDataUri);
                setFaviconIcoDataUri(icoDataUri);
                setFaviconProcessed(true);
                if (onFaviconProcessed) {
                    onFaviconProcessed(pngBlob, icoBlob);
                }
            } catch (err) {
                console.log('Failed to process favicon', err);
            }
        }, [onFaviconProcessed]);

        useEffect(() => {
            if (!treatAsFavicon || faviconProcessed || !baseImageChanged || !faviconRef.current) {
                return;
            }
            const image = faviconRef.current;
            setTimeout(() => {
                handleFaviconGeneration(image);
            }, 100);
        }, [treatAsFavicon, faviconProcessed, baseImageChanged, faviconRef, handleFaviconGeneration]);


        return (
            <Paper
                variant="outlined"
                onClick={() => inputRef.current?.click()}
                sx={{
                    p: 2,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: 1,
                    cursor: 'pointer',
                    transition: 'all 0.15s ease',
                    borderStyle: hasImage ? 'solid' : 'dashed',
                    borderColor: hasImage ? 'success.light' : 'divider',
                    backgroundColor: hasImage ? 'grey.50' : 'transparent',
                    '&:hover': {
                        borderColor: 'primary.main',
                        backgroundColor: 'action.hover',
                    },
                }}
            >
                <input
                    ref={inputRef}
                    type="file"
                    accept=".svg"
                    style={{ display: 'none' }}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                        const image = e.target.files?.length ? e.target.files[0] : null;
                        if (!image) {
                            return
                        }
                        setFaviconProcessed(false);
                        setBaseImage(image);
                        setBaseImageUrl(URL.createObjectURL(image));
                        setBaseImageChanged(true);
                        onImageChange(image);
                    }}
                />
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {imageName}
                </Typography>
                <Box
                    sx={{
                        width: '100%',
                        height: 80,
                        borderRadius: 1,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        overflow: 'hidden',
                    }}
                >
                    {hasImage ? (
                        <img
                            src={imageSrc as string}
                            alt={imageName}
                            ref={faviconRef}
                            style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
                        />
                    ) : (
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5 }}>
                            <CloudUploadOutlinedIcon sx={{ fontSize: 28, color: 'grey.400' }} />
                            <Typography variant="caption" color="text.secondary">
                                Click to upload
                            </Typography>
                        </Box>
                    )}
                </Box>
                <Typography variant="caption" color="text.secondary" sx={{ textAlign: 'center' }}>
                    {hint}
                </Typography>
                {baseImage instanceof File && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <CheckCircleOutlineIcon sx={{ fontSize: 14, color: 'success.main' }} />
                        <Typography variant="caption" color="success.main" noWrap sx={{ maxWidth: 150 }}>
                            {baseImage.name}
                        </Typography>
                    </Box>
                )}
            </Paper>
        )
    }